<script lang="ts">
export const elCarouselSizes = ['sm', 'md', 'lg'] as const;
export type ElCarouselSizes = (typeof elCarouselSizes)[number];

export interface CarouselProps {
  size?: ElCarouselSizes;
  slides: CarouselSlide[];
  opacity?: boolean;
}

export interface CarouselSlide {
  title: string;
  description: string;
  picture: string;
  button: InstanceType<typeof ElButton>['$props'];
}
</script>

<script lang="ts" setup>
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ElButton, ElIconButton } from '.';
import { computed, ref } from 'vue';
import { Device, useMatchDevice } from './utils';

defineProps<CarouselProps>();

const device = useMatchDevice();

const buttonSize = computed(() => {
  switch (device.value) {
    case Device.Desktop:
      return 'xl';
    case Device.Tablet:
      return 'l';
    default:
      return 'sm';
  }
});

const swiperInstance = ref();
const modules = ref([Navigation, Pagination, Keyboard]);
</script>

<template>
  <div
    class="w-full relative"
    :class="{
      'max-w-2xl': size === 'md',
      'max-w-7xl': size === 'lg',
    }"
  >
    <swiper
      :space-between="30"
      :slides-per-view="1"
      :keyboard="{
        enabled: true,
      }"
      :pagination="{
        clickable: true,
      }"
      :loop="true"
      :navigation="true"
      :modules="modules"
      class=""
      @swiper="swiperInstance = $event"
    >
      <swiper-slide v-for="(slide, index) in slides" :key="index">
        <div
          class="flex items-center isolate overflow-hidden w-full bg-left bg-cover bg-no-repeat bg-black rounded-2xl p-6 md:p-7 lg:p-24"
          :style="{
            backgroundImage: 'url(' + slide.picture + ')',
          }"
          :class="{
            'justify-end items-end h-auto': size === 'sm',
            'h-96': size === 'md',
            'h-auto': size === 'lg',
          }"
        >
          <div v-if="opacity" class="bg-black opacity-30 left-0 absolute w-full h-full top-0 rounded-2xl"></div>

          <div
            class="z-10 flex flex-col justify-end md:justify-center gap-3 lg:gap-4 items-start flex-1 self-stretch md:my-8 text-white text-sm md:text-base lg:text-xl"
            :class="{
              'w-full': size === 'sm',
              'max-w-80': size === 'md',
              'max-w-96': size === 'lg',
            }"
          >
            <h1 class="font-semibold text-neutral-inverse text-xl md:text-2xl lg:text-4xl">
              {{ slide.title }}
            </h1>
            <p
              class="text-neutral-inverse font-medium text-sm md:text-base md:max-w-80 lg:text-xl lg:max-w-96"
              :class="{
                'w-full': size === 'sm',
                'w-80 max-w-80': size === 'md',
                'w-96 max-w-96': size === 'lg',
              }"
            >
              {{ slide.description }}
            </p>
            <div class="py-2 md:py-3 lg:py-4">
              <ElButton v-bind="slide.button" :size="buttonSize" />
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper>
    <div v-if="slides.length > 1" class="px-6 w-full mb-20 hidden md:absolute md:mt-0 lg:top-60 md:bottom-0 lg:flex justify-between">
      <div class="w-9 h-9 flex justify-center items-center bg-white rounded-full z-10 overflow-hidden">
        <ElIconButton :icon="{ name: 'ArrowSmallLeftIcon' }" @click="swiperInstance?.slidePrev()" />
      </div>
      <div class="w-9 h-9 flex justify-center items-center bg-white rounded-full z-10 overflow-hidden">
        <ElIconButton :icon="{ name: 'ArrowSmallRightIcon' }" @click="swiperInstance?.slideNext()" />
      </div>
    </div>
  </div>
</template>
